import {Injectable, NgZone} from '@angular/core';
import * as io from 'socket.io-client';
import * as ss from 'socket.io-stream';
import {environment} from "../../environments/environment";
import {Logger} from '@common-lib';
import {Subject} from "rxjs/Subject";
import {LocalStorageService} from '@common-lib';
import {saveAs} from 'file-saver/FileSaver';

export const WsActions = {
    Upload: 'upload',
    Pause: 'pause',
    Resume: 'resume',
    Cancel: 'cancel',
    StartDownload: 'start-download',
    Download: 'download'
};


export const WsEvents = {
    StateChanged: 'event.state-changed',
    Started: 'event.started',
    Finished: 'event.finished',
};

export enum UploadStatus {
    Uploading,
    UploadFinished,
    Unzipping,
    UnzipFinished,
    Processing,
    Finished,
    Failed,
    Success
}

export enum DownloadStatus {
    Downloading,
    DownloadFinished,
    Zipping,
    ZipFinished,
    Processing,
    ProcessingFinished,
    Finished,
    Failed,
    Success
}

export const UploadStatusLabels = [
    'Uploading',
    'Uploading Finished',
    'Unzipping',
    'Unzipping Finished',
    'Processing',
    'Finished',
    'Failed',
    'Success'
];

export const DownloadStatusLabels = [
    'Downloading',
    'Download Finished',
    'Zipping',
    'Zip Finished',
    'Processing',
    'ProcessingFinished',
    'Finished',
    'Failed',
    'Success'
];

export interface UploadState {
    id: string,
    type: string,
    status: UploadStatus,
    progress: number,
    info: string
}

export interface DownloadState {
    id: string,
    type: string,
    status: DownloadStatus,
    progress: number,
    info: string,
}

@Injectable()
export class UploadAssetsService {

    private io = null;
    private subject: Subject<UploadState>;
    private inProgress = false;
    public progressStatus: Subject<boolean>;
    private receivedBinary = null;

    constructor(private logger: Logger, private storage: LocalStorageService, private zone: NgZone) {
        this.progressStatus = new Subject();
        const sessionId = this.storage.getItem('upload-sid', '');
        let url = environment.SOCKET_IO_URL;
        if (sessionId) {
            url += '?usid=' + sessionId;
        }
        this.io = io(url, {path: '/assets', secure:true});
        this.io.on(WsEvents.Started, (e: UploadState) => {
            this.logger.info('UploadAssetsService::started ', e);
        });


        this.io.on(WsEvents.Started, (e: UploadState) => {
            this.storage.putItem('upload-sid', e.id);
            this.handleStateChanged(e);
        });

        this.io.on(WsEvents.StateChanged, this.handleStateChanged.bind(this));
    }

    get uploadingProgress(): Subject<UploadState | DownloadState> {
        if (!this.subject) {
            this.subject = new Subject();
        }
        return this.subject;
    }

    uploadPackage(file: File) {
        this.inProgress = true;
        if (!this.io.connected) {
            this.io.connect();
        }
        let stream = ss.createStream();
        const data = {
            size: file.size,
            fileName: file.name
        };
        ss(this.io).emit(WsActions.Upload, stream, data);
        ss.createBlobReadStream(file).pipe(stream);
        return this.uploadingProgress;
    }

    downloadPackage() {

        this.inProgress = true;
        if (!this.io.connected) {
            this.io.connect();
        }
        const data = {
            test: "test"
        };
        this.io.emit(WsActions.StartDownload, data);

        // start uploading
        ss(this.io).once(WsActions.Download, (stream, data) => {
            this.receivedBinary = [];
            let downloaded = 0;
            let progress = 0;
            let totalSize = data.size;
            const fileName = data.fileName.replace(/\//, '');
            stream.on('data', (data) => {
                downloaded += data.length;
                progress = Math.floor(downloaded / totalSize * 100);
                this.handleStateChanged({
                    id: '',
                    type: 'download',
                    status: DownloadStatus.Downloading,
                    progress: progress,
                    info: this.bytesToSize(downloaded) + ' of ' + this.bytesToSize(totalSize)
                });
                this.receivedBinary.push(data);

            });
            stream.on('end', () => {
                let fileData = new Uint8Array(downloaded);
                let i = 0;
                this.receivedBinary.forEach(function (buff) {
                    for (let j = 0; j < buff.length; j++) {
                        fileData[i] = buff[j];
                        i++;
                    }
                });
                this.downloadFileFromBlob([fileData], fileName);
            });

            this.logger.info('socket.io ' + WsActions.Upload, data);
        });

        return this.uploadingProgress;
    }

    cancel() {
        this.storage.removeItem('upload-sid');
        this.io.emit(WsActions.Cancel);
    }

    private handleStateChanged(e: UploadState | DownloadState) {
        this.storage.putItem('upload-sid', e.id);
        //  this.logger.info('UploadAssetsService::state-changed ', e);
        this.uploadingProgress.next(e);
        if (e.status === UploadStatus.Finished || e.status === UploadStatus.Failed) {
            this.inProgress = false;
            this.storage.removeItem('upload-sid');
        } else {
            this.inProgress = true;
        }
        this.progressStatus.next(this.inProgress);
    }

    private bytesToSize(bytes) {
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes == 0) return '0 Byte';
        let i: number = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
    }

    private downloadFileFromBlob(data, fileName) {
        let a = document.createElement("a");
        a.setAttribute('style', "display: none");
        document.body.appendChild(a);
        let blob = new Blob(data, {
                type: "application/zip"
            }),
            url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);

    }
}



// WEBPACK FOOTER //
// ./src/app/services/upload-assets.service.ts